import React from "react";

const content = {
  en: {
    title: "Privacy Policy",
    updated: "Last Updated: 2025",
    sections: [
      {
        heading: "Introduction",
        text: "VAPOR prioritizes privacy protection. This policy explains how information is handled through the application. By using the app, you accept the information practices described in this policy.",
      },
      {
        heading: "Information We Do Not Collect",
        text: "Our service is designed to minimize data collection. We do not collect:",
        list: [
          "Personal identity information",
          "Contact lists or address books",
          "Message content (protected by end-to-end encryption)",
          "IP addresses (obfuscated via distributed relay nodes)",
          "Persistent identifiers that could link activities to users",
        ],
      },
      {
        heading: "Technical Information",
        text: "The application may automatically collect certain non-personal technical data to ensure service quality:",
        list: [
          "Connection metrics for relay network optimization",
          "Crash reports and performance metrics for stability improvement (opt-out available)",
          "Aggregated feature usage statistics",
        ],
        note: "This information remains fully anonymized and cannot be used to identify individual users.",
      },
      {
        heading: "Security Measures",
        text: "We employ multiple protective layers to safeguard your privacy:",
        list: [
          "End-to-end encryption for all messages",
          "Dynamic obfuscation countering traffic analysis attacks",
          "Distributed relay nodes concealing real IP addresses",
          "Optimized routing reducing message hops while maintaining security",
        ],
      },
      {
        heading: "Data Retention",
        list: [
          "Messages are stored temporarily on relay nodes until delivery",
          "Undeliverable messages are deleted automatically after 7 days",
          "App performance data is retained for 90 days in anonymized form",
          "Users can delete all local data at any time by uninstalling the app",
        ],
      },
      {
        heading: "Your Rights",
        text: "As a Vapor user, you have the right to:",
        list: [
          "Operate the service without providing any personal information",
          "Verify that your messages and metadata are not stored",
          "Opt out of all non-essential data collection",
          "Delete the app and all associated local data at any time",
        ],
      },
      {
        heading: "Changes to This Policy",
        text: "We may update this policy from time to time. Any changes will be communicated through in-app notification mechanisms. Continued use of the service after modifications constitutes your acknowledgment of the updated policy.",
      },
      {
        heading: "Contact",
        text: "If you have questions about this privacy policy, please contact us at:",
        email: "service@getvapor.org",
      },
    ],
    copyright: "© 2025 VAPOR Team. All rights reserved.",
  },
  zhTW: {
    title: "隱私權政策",
    updated: "最後更新：2025 年",
    sections: [
      {
        heading: "簡介",
        text: "VAPOR 將隱私保護視為首要任務。本政策說明應用程式如何處理相關資訊。使用本應用即表示您接受本政策中描述的資訊處理方式。",
      },
      {
        heading: "我們不會蒐集的資訊",
        text: "我們的服務旨在將資料蒐集降至最低。我們不會蒐集：",
        list: [
          "個人身份資訊",
          "通訊錄或聯絡人清單",
          "訊息內容（受端對端加密保護）",
          "IP 位址（透過分散式中繼節點混淆處理）",
          "可將活動關聯至使用者的持久性識別碼",
        ],
      },
      {
        heading: "技術資訊",
        text: "為確保服務品質，應用程式可能會自動蒐集某些非個人技術數據：",
        list: [
          "用於中繼網路最佳化的連線指標",
          "用於穩定性改善的當機報告與效能指標（可選擇退出）",
          "彙總的功能使用統計資料",
        ],
        note: "這些資訊完全匿名化處理，無法用於識別個別使用者。",
      },
      {
        heading: "安全措施",
        text: "我們採用多層防護機制來保障您的隱私：",
        list: [
          "所有訊息均採用端對端加密",
          "動態混淆技術抵禦流量分析攻擊",
          "分散式中繼節點隱藏真實 IP 位址",
          "最佳化路由在維持安全性的同時減少訊息跳轉次數",
        ],
      },
      {
        heading: "資料保留",
        list: [
          "訊息暫時儲存於中繼節點上，直到送達為止",
          "無法送達的訊息將在 7 天後自動刪除",
          "應用程式效能資料以匿名形式保留 90 天",
          "使用者可隨時透過解除安裝應用程式來刪除所有本地資料",
        ],
      },
      {
        heading: "您的權利",
        text: "作為 Vapor 使用者，您有權：",
        list: [
          "在不提供任何個人資訊的情況下使用本服務",
          "確認您的訊息和中繼資料未被儲存",
          "選擇退出所有非必要的資料蒐集",
          "隨時刪除應用程式及所有相關的本地資料",
        ],
      },
      {
        heading: "政策變更",
        text: "我們可能會不定期更新本政策。任何變更都將透過應用程式內的通知機制進行告知。在政策修改後繼續使用本服務，即表示您確認並接受更新後的政策。",
      },
      {
        heading: "聯絡方式",
        text: "如果您對本隱私權政策有任何疑問，請透過以下方式與我們聯繫：",
        email: "service@getvapor.org",
      },
    ],
    copyright: "© 2025 VAPOR 團隊。保留所有權利。",
  },
};

const PrivacyPolicy = ({ locale }) => {
  const key = locale === "zh-TW" ? "zhTW" : locale;
  const t = content[key] || content.en;

  return (
    <section className="legal-page">
      <div className="container">
        <div className="legal-header">
          <h1>{t.title}</h1>
          <p className="legal-updated">{t.updated}</p>
        </div>

        <div className="legal-content">
          {t.sections.map((section, i) => (
            <div className="legal-section" key={i}>
              <h2>{section.heading}</h2>
              {section.text && <p>{section.text}</p>}
              {section.list && (
                <ul>
                  {section.list.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              )}
              {section.note && (
                <div className="legal-note">{section.note}</div>
              )}
              {section.email && (
                <p>
                  <span className="legal-email">{section.email}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
